import type { S3ListKind } from "../S3ObjectEntry";

/**
 * Query key factory for the admin bucket-explorer queries.
 *
 * Everything the explorer reads about one bucket hangs off `s3Bucket`, so a single invalidation
 * there clears its folder lists, object pages and bindings together. The other integration
 * hooks in this directory predate the factory and key themselves.
 *
 * @see https://tkdodo.eu/blog/effective-react-query-keys#use-query-key-factories
 */
export const integrationQueryKeys = {
  all: () => ["integrations"] as const,

  s3: () => [...integrationQueryKeys.all(), "s3"] as const,

  /** The prefix for everything read about one bucket. */
  s3Bucket: (bucketName: string) =>
    [...integrationQueryKeys.s3(), "bucket", bucketName] as const,

  /**
   * One level's listing. Page tokens are chained inside the query, not part of the key.
   *
   * `pageSize` is omitted for the folders listing, which returns every folder it finds rather
   * than a page of them.
   */
  s3Listing: (
    bucketName: string,
    orgId: string,
    prefix: string,
    kind: S3ListKind,
    pageSize?: number,
  ) =>
    [
      ...integrationQueryKeys.s3Bucket(bucketName),
      "listing",
      orgId,
      prefix,
      kind,
      ...(pageSize === undefined ? [] : [pageSize]),
    ] as const,

  /** Every org a bucket name is registered to. */
  s3Bindings: (bucketName: string) =>
    [...integrationQueryKeys.s3Bucket(bucketName), "bindings"] as const,
};
